import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';

import { useFlashTransition } from 'hooks/useFlashState';
import useModal from 'components/Modals/useModal';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import { useFilterDropdownContext } from 'pages/Common/components/filterDropdownContext';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

import Overlay from 'react-bootstrap/Overlay';
import KizenTypography from 'app/kizentypo';
import styled from '@emotion/styled';
import ConfirmationModal from 'components/Modals/presets/ConfirmationModal';
import Loader from 'components/Kizen/Loader';

import {
  AddFilterButton,
  AddFilterSetButton,
  Filter,
  FilterOperatorText,
  FilterRow,
  FilterSetContainer,
  FilterSetOperationDropdown,
  useFilterErrors,
  useApiFilterErrors,
  useMappedReturnErrors,
} from 'ts-components/filters';
import {
  FilterMenu,
  FilterMenuHeader,
  FilterControlButton,
  FilterNameInput,
  ErrorCard,
  FilterControls,
  FilterNameWrapper,
} from 'ts-components/filters/components';
import { EMPTY_OBJECT } from 'utility/fieldHelpers';
import { useSharingAccessModal } from 'pages/FilterGroupsPage/hooks/useSharingAccessModal';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { SharingAccessModal } from 'components/Modals/SharingAccessModal';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import { getCanAdmin, getCanEdit } from 'components/AccessRequests/utils';
import { getOriginalError } from 'services/AxiosService';
import FilterGroupsService from 'services/FilterGroupsService';
import MachineLearningService from 'services/MachineLearningService';
import { getTimezoneOffset } from 'services/utils';
import { toastVariant, useToast } from 'components/ToastProvider';
import { FLOW_TYPE, GUARD_RAIL_CODES } from 'ts-components/Conversation';
import { AiExpandableSinglePrompt } from 'ts-components/AiExpandableSinglePrompt';
// Safety valve so a failed generated filter load cannot strand the loader
const GENERATED_FILTER_LOAD_TIMEOUT = 15000;

const CenteredLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
`;

const countFilters = (acc, [, { filters }]) => {
  // filters are counted not after adding a row but once the first dropdown (type) is selected
  return acc + filters.filter(({ type }) => type !== null).length;
};

const ClientFilterDropdown = forwardRef(function (
  {
    filterData,
    and,
    className,
    applyFiltersAction,
    updateFilterCount,
    saveGroupAction,
    deleteGroupAction,
    onResetFilter,
    setFilterName,
    filterName,
    filterGroup = EMPTY_OBJECT,
    handleChangeGroup,
    setFilterByConfig,
    clearFilter,
    numberOfFilters,
    betaPreview = false,
    objectType = 'client_client',
    errorsReturned,
    hasFilterError,
    customObjectId,
    aiFilterPrompt,
    clearAiFilterPrompt,
    groupId,
    setLastGeneratedFilterIds,
    resetLastGeneratedFilter,
    clearFilterErrors,
    generatedFilter,
    clearGeneratedFilter,
    ...others
  },
  ref
) {
  const { t } = useTranslation();
  const filterNameRef = useRef();
  const [groupError, showGroupError, flashGroupError] = useFlashTransition();
  const [showToast] = useToast();
  const { aiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.filter_builder
  );
  const {
    filterSets,
    meta: {
      addFilter,
      addFilterSet,
      build,
      validate,
      removeFilter,
      reset,
      setFilterSetOperation,
      hasAnyValidFiltersTypes,
    },
    setGeneratingFilter,
    generatingFilter,
    setDirtyState,
  } = useFilterDropdownContext();

  const isContactsPage = objectType === 'client_client';
  const lastGenerationFlowId = useSelector((s) =>
    isContactsPage
      ? s.contactPage.filters.lastGeneratedFilterFlowId
      : s.recordsPage.filters.lastGeneratedFilterFlowId
  );
  const lastGenerationSessionId = useSelector((s) =>
    isContactsPage
      ? s.contactPage.filters.lastGeneratedFilterSessionId
      : s.recordsPage.filters.lastGeneratedFilterSessionId
  );

  const errorsDictionary = useMappedReturnErrors(errorsReturned, filterSets);
  const [apiErrors, setApiErrors] = useApiFilterErrors(
    errorsDictionary,
    errorsReturned?.timestamp,
    3000
  );

  const [filterErrors, setFilterErrors] = useFilterErrors();
  const [operation, setOperation] = useState(and ? 'and' : 'or');
  const [isDeleteAction, setDeleteAction] = useState(false);
  const multipleSets = filterSets.length > 1;

  const isJustOneFilter =
    !multipleSets &&
    (filterSets.length === 0 || filterSets[0][1]?.filters?.length === 1);

  const handleClearFilter = useCallback(() => {
    reset();
    clearFilter();
    setDirtyState(false);
    setApiErrors({});
    resetLastGeneratedFilter();
  }, [
    clearFilter,
    reset,
    setApiErrors,
    setDirtyState,
    resetLastGeneratedFilter,
  ]);

  const handleApplyFilter = () => {
    if (isJustOneFilter && !groupId && !filterSets[0][1]?.filters?.[0]?.type) {
      onResetFilter();
      return;
    }
    const { errors, hasErrors } = validate({ isEmptyFilterTypeInvalid: false });
    setApiErrors({});
    if (hasErrors) {
      setFilterErrors(errors);
    } else {
      applyFiltersAction(build(operation === 'and'));
    }
  };

  const [confirmationModalProps, , confirmationModal] = useModal({
    handleSubmit: isDeleteAction
      ? () => {
          setDirtyState(false);
          deleteGroupAction();
        }
      : handleClearFilter,
    ...(isDeleteAction ? { handleHide: () => setDeleteAction(false) } : {}),
  });

  const extractGenerationResult = useCallback((response) => {
    const guardrailCode = response.run_metadata?.filter_builder?.guardrail_code;
    const guardrailMessage =
      response.run_metadata?.filter_builder?.guardrail_message;
    const sessionId =
      response.session_id ??
      response.run_metadata?.filter_builder?.fallback_session_id;
    const generatedFilter =
      Object.keys(response.run_metadata?.filter_builder?.filter_config || {})
        .length > 0 && response.run_metadata?.filter_builder?.filter_config;

    return {
      hasResult: !!generatedFilter,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const incrementFilterCount = () => {
    updateFilterCount(filterSets.reduce(countFilters, 1));
  };

  const decrementFilterCount = () => {
    updateFilterCount(filterSets.reduce(countFilters, -1));
  };

  const clearFilters = useCallback(() => {
    setApiErrors({});
    if (errorsReturned?.parsedErrors?.length) {
      clearFilterErrors();
    }
  }, [errorsReturned?.parsedErrors?.length, setApiErrors, clearFilterErrors]);

  const handleSaveFilterGroup = async () => {
    if (!filterName) {
      flashGroupError(t('Please type a group name before saving.'));
    } else {
      const { errors, hasErrors } = validate(!hasAnyValidFiltersTypes);
      setApiErrors({});
      if (hasErrors) {
        setFilterErrors(errors);
      } else {
        try {
          setDirtyState(false);
          const filterGroupUpdated =
            await FilterGroupsService.updateFilterGroup(
              customObjectId,
              filterGroup.id,
              {
                name: filterName,
                config: build(operation === 'and'),
              }
            );
          saveGroupAction({
            filterGroup: filterGroupUpdated,
            isPatch: true,
          });
        } catch (error) {
          const orig = getOriginalError(error);
          showToast({
            message: t('Filter Group Not Saved'),
            variant: toastVariant.FAILURE,
          });
          if (orig.name) {
            flashGroupError(orig.name);
          }
        }
      }
    }
  };

  const createFilterGroup = useCallback(
    async ({ name, permissions, isPrivate }) => {
      const { errors, hasErrors } = validate(!hasAnyValidFiltersTypes);
      setApiErrors({});
      if (hasErrors) {
        setFilterErrors(errors);
      } else {
        setDirtyState(false);
        const filterGroupCreated = await FilterGroupsService.createFilterGroup(
          customObjectId,
          {
            name,
            config: build(operation === 'and'),
            sharingSettings: {
              ...buildSharingSettingsForAPI(permissions).sharing_settings,
              private: isPrivate,
            },
          }
        );
        setFilterName(name);
        saveGroupAction({
          filterGroup: filterGroupCreated,
          isPatch: false,
        });
      }
    },
    [
      setDirtyState,
      hasAnyValidFiltersTypes,
      validate,
      setApiErrors,
      setFilterErrors,
      customObjectId,
      build,
      operation,
      setFilterName,
      saveGroupAction,
    ]
  );

  const { sharingAccessModalProps, onShowSharingAccessModal } =
    useSharingAccessModal(createFilterGroup);

  const handleCreateFilterGroup = async () => {
    const { errors, hasErrors } = validate(!hasAnyValidFiltersTypes);
    setApiErrors({});
    if (hasErrors) {
      setFilterErrors(errors);
    } else {
      onShowSharingAccessModal();
    }
  };

  // There's CSS that prevents the error underline from displaying when the input is focused
  // We want to focus the input and also flash an error, so this will only focus after hasFilterError
  // has changed. This won't run on the first render thanks to the behavior of react-use's useUpdateEffect
  useUpdateEffect(() => {
    if (!groupError && filterNameRef.current) {
      filterNameRef.current.focus();
    }
  }, [groupError]);

  const confirmMessage = isDeleteAction
    ? t('This will permanently delete the Filter Group.')
    : t('Your filters are unsaved, this will clear your work.');

  const [nameTooltipProps, nameTooltip] = useTruncationTooltip({
    label: filterName,
  });

  const [canAdmin, canEdit] = useMemo(
    () => [getCanAdmin(filterGroup), getCanEdit(filterGroup)],
    [filterGroup]
  );

  const handleChangeGroupWithReset = useCallback(
    (groupData) => {
      if (handleChangeGroup) {
        handleChangeGroup(groupData);
        resetLastGeneratedFilter();
      }
    },
    [handleChangeGroup, resetLastGeneratedFilter]
  );

  const applyGeneratedFilter = useCallback(
    (filterName, filterConfig) => {
      setDirtyState(true);
      setFilterByConfig(filterConfig);
      applyFiltersAction(filterConfig);
      setFilterName(filterName);
      filterSets[0]?.[1]?.filters?.forEach(({ ops }) => {
        ops.update();
      });
    },
    [
      setDirtyState,
      setFilterByConfig,
      applyFiltersAction,
      setFilterName,
      filterSets,
    ]
  );

  // Apply an externally provided generated filter (e.g. from AI chat preview
  // navigation) through the same flow as inline AI filter generation.
  // Loading the filter config is async (field metadata requests), so show a
  // loading state until the loaded config lands in the filter sets.
  const [applyingGeneratedFilter, setApplyingGeneratedFilter] = useState(false);
  const appliedGeneratedFilterRef = useRef(null);
  const generatedFilterTimeoutRef = useRef(null);

  useEffect(() => {
    if (!generatedFilter?.filterConfig) return;
    if (appliedGeneratedFilterRef.current === generatedFilter) return;
    appliedGeneratedFilterRef.current = generatedFilter;
    setApplyingGeneratedFilter(true);
    applyGeneratedFilter(
      generatedFilter.filterName,
      generatedFilter.filterConfig
    );
    clearGeneratedFilter?.();
    generatedFilterTimeoutRef.current = setTimeout(() => {
      setApplyingGeneratedFilter(false);
    }, GENERATED_FILTER_LOAD_TIMEOUT);
  }, [generatedFilter, applyGeneratedFilter, clearGeneratedFilter]);

  // The generated filter has finished loading once the filter sets contain
  // valid filter types
  useEffect(() => {
    if (!applyingGeneratedFilter || !hasAnyValidFiltersTypes) return;
    clearTimeout(generatedFilterTimeoutRef.current);
    setApplyingGeneratedFilter(false);
  }, [applyingGeneratedFilter, hasAnyValidFiltersTypes]);

  useEffect(() => {
    return () => clearTimeout(generatedFilterTimeoutRef.current);
  }, []);

  const onChatSessionSuccess = useCallback(
    (flowStatusData) => {
      const sessionId = flowStatusData?.session_id || flowStatusData?.id;
      const flowId = flowStatusData?.flow_id || flowStatusData?.id;

      if (sessionId && flowId) {
        setLastGeneratedFilterIds({ flowId, sessionId });
      }

      if (flowStatusData?.next_actions?.[0]?.metadata) {
        showToast({
          message: t('Filter Generated Successfully'),
          variant: toastVariant.SUCCESS,
        });
        const generatedFilterMetdata = flowStatusData.next_actions[0].metadata;
        applyGeneratedFilter(
          generatedFilterMetdata.filter_name,
          generatedFilterMetdata.filter_config
        );
        return;
      }

      const navigateUrl = flowStatusData?.next_actions?.[0]?.url;
      if (navigateUrl) {
        try {
          const url = new URL(navigateUrl);
          const relativePath = url.pathname + url.search;
          const groupId = url.searchParams.get('group');

          if (groupId && handleChangeGroup) {
            handleChangeGroupWithReset({ value: groupId, refetch: true });
          }
          showToast({
            message: t('Filter Generated Successfully'),
            variant: toastVariant.SUCCESS,
          });
          history.push(relativePath);
          return;
        } catch (error) {
          monitoringExceptionHelper(error);
        }
      }
      showToast({
        message: t('Filter Generation Failed, Please try again later'),
        variant: toastVariant.FAILURE,
      });
    },
    [
      applyGeneratedFilter,
      handleChangeGroup,
      handleChangeGroupWithReset,
      showToast,
      t,
      setLastGeneratedFilterIds,
    ]
  );

  const onOneShotSuccess = useCallback(
    (flowStatusData) => {
      const sessionId = flowStatusData?.session_id;
      const flowId = flowStatusData?.flow_id || flowStatusData?.id;

      if (sessionId || flowId) {
        setLastGeneratedFilterIds({ flowId, sessionId });
      }

      const guardrailCode =
        flowStatusData.run_metadata.filter_builder?.guardrail_code;
      const guardrailMessage =
        flowStatusData.run_metadata.filter_builder?.guardrail_message;
      const generatedFilter =
        Object.keys(
          flowStatusData.run_metadata?.filter_builder?.filter_config || {}
        ).length > 0 &&
        flowStatusData.run_metadata?.filter_builder?.filter_config;
      const generatedFilterName =
        flowStatusData.run_metadata?.filter_builder?.filter_name;

      if (guardrailCode && guardrailMessage) {
        showToast({
          message: guardrailMessage,
          variant:
            guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
              ? toastVariant.ALERT
              : toastVariant.FAILURE,
          autohide: false,
        });
        if (generatedFilter) {
          applyGeneratedFilter(generatedFilterName, generatedFilter);
        }
      } else if (generatedFilter) {
        showToast({
          message: t('Filter Generated Successfully'),
          variant: toastVariant.SUCCESS,
        });
        applyGeneratedFilter(generatedFilterName, generatedFilter);
      } else {
        showToast({
          message: t('Filter Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [applyGeneratedFilter, showToast, t, setLastGeneratedFilterIds]
  );
  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          message: usageError[0],
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Filter Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t]
  );

  // Detect errors and flag the flow/chat
  useEffect(() => {
    if (hasFilterError && (lastGenerationSessionId || lastGenerationFlowId)) {
      const reason = 'Generated filter resulted in API validation error';
      if (lastGenerationSessionId) {
        MachineLearningService.createChatFlag({
          chatId: lastGenerationSessionId,
          tag: 'error',
          reason,
          options: {},
        }).catch((error) => {
          monitoringExceptionHelper(error);
        });
      }

      if (lastGenerationFlowId) {
        MachineLearningService.createFlowFlag({
          flowId: lastGenerationFlowId,
          tag: 'error',
          reason,
          options: {},
        }).catch((error) => {
          monitoringExceptionHelper(error);
        });
      }

      resetLastGeneratedFilter();
    }
  }, [
    hasFilterError,
    errorsReturned,
    lastGenerationSessionId,
    lastGenerationFlowId,
    resetLastGeneratedFilter,
  ]);

  return (
    <>
      <div ref={ref} className={className}>
        <FilterMenu {...others}>
          <FilterMenuHeader>
            <FilterNameWrapper>
              <FilterNameInput
                ref={filterNameRef}
                placeholder={t('Enter Filter Group Name')}
                value={filterName}
                onChange={setFilterName}
                error={!!groupError}
                debounce
                {...nameTooltipProps}
              />
              {aiFeaturesEnabled ? (
                <AiExpandableSinglePrompt
                  flowType={FLOW_TYPE.filter_builder_inline}
                  customObjectId={customObjectId}
                  onOneShotSuccess={onOneShotSuccess}
                  onChatSessionSuccess={onChatSessionSuccess}
                  onGenerationFailure={onGenerationFailure}
                  setIsGenerating={setGeneratingFilter}
                  entityName="Filter"
                  importResults={false}
                  extractGenerationResult={extractGenerationResult}
                  messagesConfig={{
                    initialMessage: t(
                      'What would you like to filter for your "{{custom_object_name}}" object?',
                      {
                        custom_object_name: others.customObjectName,
                      }
                    ),
                    placeholder: t('Describe your filter here'),
                    buildingMessage: t('Filter is Building'),
                    successMessage: t('Filter Generated Successfully'),
                    failureMessage: t(
                      'Filter Generation Failed, Please try again later'
                    ),
                    expandedPlaceholder: t('Add Additional Context'),
                    closingMessage: t('Closing session'),
                  }}
                  context={{
                    filter_name: filterName,
                    filter_timezone_offset: getTimezoneOffset(),
                  }}
                  placeholder={t(
                    'What would you like to filter for your "{{custom_object_name}}" object?',
                    {
                      custom_object_name: others.customObjectName,
                    }
                  )}
                  inModal
                />
              ) : null}
            </FilterNameWrapper>
            {filterName ? nameTooltip : null}
            <Overlay
              transition={false}
              target={filterNameRef.current}
              show={groupError}
              placement="bottom-start"
            >
              <ErrorCard show={showGroupError} duration="300ms">
                <KizenTypography>{groupError}</KizenTypography>
              </ErrorCard>
            </Overlay>
            <FilterControls>
              <FilterControlButton
                variant="outline"
                color="blue"
                onClick={handleApplyFilter}
                disabled={!!generatingFilter}
                data-qa="apply-filter"
              >
                {t('Apply Filter')}
              </FilterControlButton>
              {filterGroup?.id && canEdit ? (
                <FilterControlButton
                  variant="outline"
                  color="green"
                  disabled={!!generatingFilter}
                  onClick={handleSaveFilterGroup}
                  data-qa="save-group"
                >
                  {t('Save Group')}
                </FilterControlButton>
              ) : null}
              <FilterControlButton
                variant="outline"
                color="green"
                disabled={!!generatingFilter}
                onClick={handleCreateFilterGroup}
                data-qa="create-group"
              >
                {t('Create New Group')}
              </FilterControlButton>
              {filterGroup?.id && canAdmin ? (
                <FilterControlButton
                  variant="outline"
                  color="red"
                  disabled={!!generatingFilter}
                  onClick={() => {
                    setDeleteAction(true);
                    confirmationModal.show();
                  }}
                >
                  {t('Delete Group')}
                </FilterControlButton>
              ) : null}
              <FilterControlButton
                variant="outline"
                color="red"
                disabled={!!generatingFilter}
                onClick={() => {
                  confirmationModal.show();
                }}
                data-qa="clear-filter"
              >
                {t('Clear All Filters')}
              </FilterControlButton>
            </FilterControls>
          </FilterMenuHeader>
          {applyingGeneratedFilter ? (
            <CenteredLoader>
              <Loader loading paddingTop="0" />
            </CenteredLoader>
          ) : (
            <>
              {filterSets.map(([setId, { and, filters }], setIdx) => {
            const multipleFilters = filters.length > 1;
            return (
              <Fragment key={setId}>
                {setIdx > 0 && (
                  <FilterSetOperationDropdown
                    operation={operation}
                    onChange={setOperation}
                    disabled={!!generatingFilter}
                  />
                )}
                <FilterSetContainer
                  value={and ? 'all' : 'any'}
                  showBorder={multipleFilters || multipleSets}
                  onChange={(value) =>
                    setFilterSetOperation(setId, value === 'all')
                  }
                >
                  {filters.map(({ type, steps, ops, id }, idx, arr) => {
                    const isLastFilter = idx === arr.length - 1;
                    const filterTypeChosen = type !== null;
                    const errors = filterErrors[setId]?.[idx] ?? {};

                    if (apiErrors[setId]?.[idx]) {
                      errors.filter_type = t('This filter is invalid.');
                    }

                    return (
                      <FilterRow key={id} data-qa="filter-row">
                        <Filter
                          disabled={!!generatingFilter}
                          showDeleteIcon
                          disableSelect={!!generatingFilter}
                          objectType={objectType}
                          steps={steps}
                          next={ops.next}
                          set={ops.set}
                          errors={errors}
                          onChange={(step, _value) => {
                            setDirtyState(true);
                            if (steps.length === 1 && step === 'filter_type') {
                              incrementFilterCount();
                            }

                            clearFilters();
                            ops.update();
                          }}
                          onDelete={() => {
                            if (multipleSets || multipleFilters) {
                              // multiple filters old rules apply
                              decrementFilterCount();
                              removeFilter(setId, idx);
                            } else {
                              // reset the filter
                              reset();
                            }
                          }}
                        />
                        {arr.length > 1 && !isLastFilter && (
                          <FilterOperatorText and={and} />
                        )}
                        {filterTypeChosen && isLastFilter && (
                          <AddFilterButton onClick={() => addFilter(setId)} />
                        )}
                      </FilterRow>
                    );
                  })}
                </FilterSetContainer>
              </Fragment>
            );
          })}
              <AddFilterSetButton
                disabled={!!generatingFilter}
                topMargin={
                  generatingFilter
                    ? false
                    : multipleSets ||
                      (filterSets.length &&
                        filterSets[0][1].filters.length > 1)
                }
                onClick={(value) => {
                  addFilterSet();
                  if (!multipleSets) {
                    setOperation(value);
                  }
                }}
              />
            </>
          )}
        </FilterMenu>
      </div>
      <ConfirmationModal
        buttonText={isDeleteAction ? t('Confirm Delete') : t('Confirm Clear')}
        heading={
          isDeleteAction ? t('Please Confirm Deletion') : t('Please Confirm')
        }
        {...confirmationModalProps}
        data-qa="clear-confirm-modal"
      >
        {confirmMessage}
      </ConfirmationModal>
      {sharingAccessModalProps.show ? (
        <SharingAccessModal
          showPrivateToggle
          existing={{ name: filterName }}
          {...sharingAccessModalProps}
          instanceName={t('Filter Group')}
          isTemplate={false}
          forceAdmin
        />
      ) : null}
    </>
  );
});

export default ClientFilterDropdown;
